import { useMutation } from '@tanstack/react-query';
import { applicationFormApi } from 'api/applicationForm';
import { Button, FormCurrencyField } from 'components/common';
import { useNavigateBack } from 'hooks';
import { useApplicationInProgressById } from 'queries/applications/useApplicationInProgressById';
import { useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useParams, useSearchParams } from 'react-router-dom';
import { getCurrentRoute } from 'utils/location';
import { parseApplicationInProgress } from '../helpers';
import { ApplicationFormPageEnum } from '../types';
import { incomeQuestions } from './questions';
import { IIncomeForm, IIncomeQuestions } from './types';

export const Income = () => {
  const intl = useIntl();
  const [searchParams, setSearchParams] = useSearchParams();
  const employment = (searchParams.get('employment') || '') as keyof IIncomeQuestions;
  const hasSecondJobParam = searchParams.get('hasSecondJob');
  const navigateBack = useNavigateBack();
  const { id: applicationId = '' } = useParams();
  const currentRoute = getCurrentRoute();
  const { mutate: patchApplicationInProgress } = useMutation(applicationFormApi.patchApplicationInProgress);
  const { data: applicationInProgress } = useApplicationInProgressById(applicationId);
  const formattedResponse =
    applicationInProgress?.[currentRoute] && parseApplicationInProgress(applicationInProgress[currentRoute]);
  const yearsRequirementFulfilled = searchParams.get('yearsRequirementFulfilled') === 'true';
  const requiredFieldErrorMessage = intl.formatMessage({ id: 'common.error.requiredField' });

  const form = useForm<IIncomeForm>({
    defaultValues: {
      annualIncome: '',
      dividends: '',
    },
  });

  useEffect(() => {
    if (formattedResponse) form.reset(formattedResponse);
  }, [applicationInProgress]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const submitFormHandler = async (formData: IIncomeForm) => {
    const filteredFormData = Object.entries(formData).filter(([, value]) => value);
    const questions = filteredFormData.map(([key, answer]) => ({
      question: intl.formatMessage({ id: incomeQuestions[employment][key].labelKey }),
      key,
      answer,
    }));

    await patchApplicationInProgress({ questions, currentRoute, applicationId });

    const journeyHasBonusAndCommissions = [
      'EMPLOYEE_PERMANENT_no',
      'EMPLOYEE_PERMANENT_yes',
      'EMPLOYEE_CONTRACTOR',
      'SELF_EMPLOYED_LIMITED_COMPANY_BELOW',
    ].includes(employment);

    const theFollowingPageForNonBonusAndCommissionsApplication = hasSecondJobParam
      ? ApplicationFormPageEnum.OTHER_INCOME
      : ApplicationFormPageEnum.SECOND_JOB;

    const nextPage =
      journeyHasBonusAndCommissions && employment !== 'EMPLOYEE_CONTRACTOR'
        ? ApplicationFormPageEnum.BONUS_AND_COMMISSIONS
        : theFollowingPageForNonBonusAndCommissionsApplication;

    searchParams.set('section', nextPage);
    setSearchParams(searchParams);
  };

  // @TODO: needs to be refactored
  if (!incomeQuestions[employment]) return <p>Something went wrong</p>;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(submitFormHandler)}>
        <section>
          <FormattedMessage id={incomeQuestions[employment].annualIncome.labelKey} tagName={'h4'} />
          {incomeQuestions[employment].annualIncome.explanationKey && (
            <FormattedMessage id={incomeQuestions[employment].annualIncome.explanationKey} tagName={'p'} />
          )}

          <FormCurrencyField name={'annualIncome'} rules={{ required: requiredFieldErrorMessage }} />
        </section>
        {yearsRequirementFulfilled && incomeQuestions[employment].prevYearAnnualIncome && (
          <section>
            <FormattedMessage id={incomeQuestions[employment].prevYearAnnualIncome.labelKey} tagName={'h4'} />
            <FormCurrencyField name={'prevYearAnnualIncome'} rules={{ required: requiredFieldErrorMessage }} />
          </section>
        )}
        {incomeQuestions[employment].dividends && (
          <section>
            <FormattedMessage id={incomeQuestions[employment].dividends.labelKey} tagName={'h4'} />
            {incomeQuestions[employment].dividends.explanationKey && (
              <FormattedMessage id={incomeQuestions[employment].dividends.explanationKey} tagName={'p'} />
            )}

            <FormCurrencyField name={'dividends'} rules={{ required: requiredFieldErrorMessage }} />
          </section>
        )}
        {yearsRequirementFulfilled && incomeQuestions[employment].prevYearDividends && (
          <section>
            <FormattedMessage id={incomeQuestions[employment].prevYearDividends.labelKey} tagName={'h4'} />
            <FormCurrencyField name={'prevYearDividends'} rules={{ required: requiredFieldErrorMessage }} />
          </section>
        )}

        <div className={'mt-8 flex justify-between'}>
          <Button variant={'outlined'} onClick={navigateBack}>
            <FormattedMessage id={'cta.back'} />
          </Button>

          <Button variant={'contained'} type={'submit'}>
            <FormattedMessage id={'cta.continue'} />
          </Button>
        </div>
      </form>
    </FormProvider>
  );
};
